import { FC } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAddressCard,
  faAddressBook,
  faPlug,
  faBagsShopping,
  faImages,
  faEnvelope,
  faClone,
  faUser,
  faHomeAlt,
} from '@fortawesome/pro-solid-svg-icons';

import { NavItems } from '@hooks/use-navigation-context';
import { UserContextValues } from '@context/user-context';

import { useWorkspaceUrls } from '@modules/use-workspace-urls';
import { isWorkspaceEmailSignaturesEnabled } from '@modules/user-helpers';

import { Stack } from '@ui-lib';

import { useIsActive } from '../hooks';

import { NavIcon, NavIconText, NavItem, NavItemDivider, NavItemLoadingPlaceholder } from '../NavItem';
import { useFeatureFlag } from '@modules/feature-flag';

interface NavListProps {
  isTeamAdmin: boolean;
  userDetails: UserContextValues;
}

const LoadingSkeleton: React.FC = () => {
  return (
    <Stack gap="small">
      <NavItemLoadingPlaceholder />
      <NavItemLoadingPlaceholder />
      <NavItemLoadingPlaceholder />
      <NavItemLoadingPlaceholder />
    </Stack>
  );
};

const Links: FC<React.PropsWithChildren<NavListProps>> = ({ isTeamAdmin, userDetails }) => {
  const {
    personalCardsGraphQL,
    cardsTeamGraphQLUrl,
    emailSignatureUrl,
    accessoriesUrl,
    virtualBackgroundsUrl,
    contactsUrl,
    teamMembersUrl,
    templatesV3Url,
    homeUrl,
    integrationCardProvisioning,
  } = useWorkspaceUrls();

  const isActive = useIsActive();
  const integrationsGlowUp = useFeatureFlag('integrations-glow-up', 'off');
  const showEmailSignaturesNavItem = isWorkspaceEmailSignaturesEnabled(userDetails);
  const showPersonalCardsNavItem = !isTeamAdmin;

  return (
    <ul>
      {isTeamAdmin && (
        <>
          <NavItem label="Home" highlightId="home" href={homeUrl} selected={isActive(NavItems.Home)}>
            <NavIcon>
              <FontAwesomeIcon icon={faHomeAlt} />
            </NavIcon>
            <NavIconText>Home</NavIconText>
          </NavItem>
          <NavItemDivider />
        </>
      )}

      {showPersonalCardsNavItem && (
        <NavItem
          label="Cards"
          highlightId={'card'}
          href={personalCardsGraphQL}
          selected={isActive(NavItems.PersonalCardsGraphQL)}
        >
          <NavIcon>
            <FontAwesomeIcon icon={faAddressCard} />
          </NavIcon>
          <NavIconText>Cards</NavIconText>
        </NavItem>
      )}

      {isTeamAdmin && (
        <NavItem
          label={'Team Cards'}
          highlightId={'team-card'}
          href={cardsTeamGraphQLUrl}
          selected={isActive(NavItems.CardsTeamGraphQL)}
        >
          <NavIcon>
            <FontAwesomeIcon icon={faAddressCard} />
          </NavIcon>
          <NavIconText>Cards</NavIconText>
        </NavItem>
      )}

      {showEmailSignaturesNavItem && (
        <NavItem
          label={'Email Signatures'}
          highlightId={'email-signature'}
          href={emailSignatureUrl}
          selected={isActive(NavItems.EmailSignatures)}
        >
          <NavIcon>
            <FontAwesomeIcon icon={faEnvelope} />
          </NavIcon>
          <NavIconText>Email Signatures</NavIconText>
        </NavItem>
      )}

      <NavItem
        label={'Virtual Backgrounds'}
        href={virtualBackgroundsUrl}
        selected={isActive(NavItems.VirtualBackgrounds)}
      >
        <NavIcon>
          <FontAwesomeIcon icon={faImages} />
        </NavIcon>
        <NavIconText>Virtual Backgrounds</NavIconText>
      </NavItem>

      <NavItem label={'Accessories'} href={accessoriesUrl} selected={isActive(NavItems.Accessories)}>
        <NavIcon>
          <FontAwesomeIcon icon={faBagsShopping} />
        </NavIcon>
        <NavIconText>Accessories</NavIconText>
      </NavItem>
      <NavItemDivider />

      <NavItem label={'Contacts'} href={contactsUrl} selected={isActive(NavItems.Contacts)}>
        <NavIcon>
          <FontAwesomeIcon icon={faAddressBook} />
        </NavIcon>
        <NavIconText>Contacts</NavIconText>
      </NavItem>

      {isTeamAdmin && integrationsGlowUp && (
        <NavItem
          label={'Integrations'}
          href={integrationCardProvisioning}
          selected={isActive(NavItems.Integrations)}
        >
          <NavIcon>
            <FontAwesomeIcon icon={faPlug} />
          </NavIcon>
          <NavIconText>Integrations</NavIconText>
        </NavItem>
      )}

      <NavItemDivider />

      {isTeamAdmin && (
        <>
          <NavItem
            label={'Team members'}
            highlightId={'team-member'}
            href={teamMembersUrl}
            selected={isActive(NavItems.TeamMembers)}
          >
            <NavIcon>
              <FontAwesomeIcon icon={faUser} />
            </NavIcon>
            <NavIconText>Team Members</NavIconText>
          </NavItem>

          <NavItem label={'Templates'} href={templatesV3Url} selected={isActive(NavItems.TemplatesV3)}>
            <NavIcon>
              <FontAwesomeIcon icon={faClone} />
            </NavIcon>
            <NavIconText>Templates</NavIconText>
          </NavItem>
          <NavItemDivider />
        </>
      )}
    </ul>
  );
};

const NavList = ({
  loading,
  userDetails,
  isTeamAdmin,
}: {
  loading: boolean;
  userDetails: UserContextValues;
  isTeamAdmin: boolean;
}) => {
  if (loading) {
    return <LoadingSkeleton />;
  }
  return <Links isTeamAdmin={isTeamAdmin} userDetails={userDetails} />;
};

export default NavList;
